/**
 * @fileoverview Página responsável por centralizar as configurações operacionais e administrativas da conta.
 */

import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { makeStyles, Paper, Tabs, Tab, CircularProgress } from "@material-ui/core";

import TabPanel from "../../components/TabPanel";

import SchedulesForm from "../../components/SchedulesForm";
import ScheduleExceptionsManager from "../../components/ScheduleExceptionsManager";
import HelpsManager from "../../components/HelpsManager";
import JobRolesManager from "../../components/JobRolesManager";
import Options from "../../components/Settings/Options";
import MfaPolicyManager from "../../components/MfaPolicyManager";
import MfaPersonalManager from "../../components/MfaPersonalManager";
import CompanyDataForm from "../../components/CompanyDataForm";
import BillingPanel from "../../components/BillingPanel";
import { i18n } from "../../translate/i18n.js";
import { toast } from "react-toastify";

import useCompanies from "../../hooks/useCompanies";
import useAuth from "../../hooks/useAuth.js";
import useSettings from "../../hooks/useSettings";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(4),
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    width: '100%',
    maxWidth: '100%',
    minHeight: 'calc(100vh - 64px)',
    backgroundColor: theme.mode === 'light' ? '#f4f6fa' : 'transparent',
  },
  tabsContainer: {
    marginBottom: theme.spacing(3),
    backgroundColor: theme.mode === 'light'
      ? '#4376FF'
      : 'rgba(18, 18, 19, 0.6)',
    borderRadius: '16px',
    border: theme.mode === 'dark' ? '1px solid rgba(91, 124, 255, 0.2)' : 'none',
    boxShadow: theme.mode === 'light'
      ? '0 2px 8px rgba(61, 92, 253, 0.25)'
      : '0 8px 32px 0 rgba(0, 0, 0, 0.5)',
    '& .MuiTab-root': {
      color: theme.mode === 'light' ? 'rgba(255, 255, 255, 0.7)' : '#A1A1AA',
      fontWeight: 500,
      fontSize: '0.9rem',
      textTransform: 'none',
      '&.Mui-selected': {
        color: theme.mode === 'light' ? '#ffffff' : '#5B7CFF',
      },
    },
    '& .MuiTabs-indicator': {
      backgroundColor: theme.mode === 'light' ? '#ffffff' : '#5B7CFF',
      height: '3px',
      borderRadius: '3px 3px 0 0',
    },
  },
  tabContent: {
    width: "100%",
  },
}));

const SettingsCustom = () => {
  const classes = useStyles();
  const history = useHistory();
  const [tab, setTab] = useState("options");
  const [schedules, setSchedules] = useState([]);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState({});
  const [settings, setSettings] = useState({});
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);

  const isInternalOnly = currentUser?.accessScope === "internal";
  const isAdminOrSuper = currentUser?.profile === "admin" || currentUser?.super;

  // Deep-link do onboarding: /settings#horarios abre direto a aba Horários.
  useEffect(() => {
    if (history.location.hash === "#horarios") {
      setTab("schedules");
    }
  }, [history.location.hash]);

  const { getCurrentUserInfo } = useAuth();
  const { find, updateSchedules } = useCompanies();
  const { getAll: getAllSettings } = useSettings();

  useEffect(() => {
    async function findData() {
      setLoading(true);
      try {
        let companyId = localStorage.getItem("companyId");

        // Sem companyId em cache: precisa buscar o usuário primeiro para
        // descobrir a empresa antes de paralelizar os demais fetchs.
        if (!companyId) {
          const user = await getCurrentUserInfo();

          if (!user) {
            toast.error("Não foi possível obter informações do usuário. Faça login novamente.");
            history.push("/login");
            return;
          }

          setCurrentUser(user);

          if (user.companyId) {
            companyId = user.companyId;
            localStorage.setItem("companyId", companyId);
          } else {
            toast.error("Usuário sem empresa vinculada. Entre em contato com o suporte.");
            return;
          }

          const [company, settingList] = await Promise.all([
            find(companyId),
            getAllSettings(),
          ]);
          setCompany(company);
          setSchedules(company.schedules);
          setSettings(settingList);

          if (Array.isArray(settingList)) {
            const scheduleType = settingList.find((d) => d.key === "scheduleType");
            if (scheduleType) {
              setSchedulesEnabled(scheduleType.value === "company");
            }
          }
        } else {
          // Caminho quente: dispara os três fetchs em paralelo.
          const [user, company, settingList] = await Promise.all([
            getCurrentUserInfo(),
            find(companyId),
            getAllSettings(),
          ]);

          if (user) setCurrentUser(user);
          setCompany(company);
          setSchedules(company.schedules);
          setSettings(settingList);

          if (Array.isArray(settingList)) {
            const scheduleType = settingList.find((d) => d.key === "scheduleType");
            if (scheduleType) {
              setSchedulesEnabled(scheduleType.value === "company");
            }
          }
        }
      } catch (e) {
        console.error("Erro geral no SettingsCustom:", e);
        toast.error("Erro ao carregar configurações");
      }
      setLoading(false);
    }
    findData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  useEffect(() => {
    if (isInternalOnly) {
      setTab("security");
    }
  }, [isInternalOnly]);

  const handleSubmitSchedules = async (data) => {
    setLoading(true);
    try {
      setSchedules(data);
      await updateSchedules({ id: company.id, schedules: data });
      toast.success(i18n.t("settings.schedulesUpdated"));
    } catch (e) {
      toast.error(e);
    }
    setLoading(false);
  };

  return (
    <MainContainer>
      <div className={classes.container}>
        {/* Tabs Container */}
        <div className={classes.tabsContainer}>
          <Tabs
            value={tab}
            indicatorColor="primary"
            textColor="primary"
            scrollButtons="on"
            variant="scrollable"
            onChange={handleTabChange}
          >
            {!isInternalOnly && (
              <Tab label={i18n.t("settings.tabs.options")} value={"options"} />
            )}
            {!isInternalOnly && isAdminOrSuper && (
              <Tab label={i18n.t("settings.tabs.jobRoles")} value={"jobRoles"} />
            )}
            <Tab label={i18n.t("settings.tabs.security") || "Segurança"} value={"security"} />
            {!isInternalOnly && (loading || schedulesEnabled) && (
              <Tab label={i18n.t("settings.tabs.schedules")} value={"schedules"} />
            )}
            {!isInternalOnly && isAdminOrSuper && (
              <Tab label={i18n.t("settings.tabs.companyData") || "Dados da Empresa"} value={"companyData"} />
            )}
            {!isInternalOnly && isAdminOrSuper && (
              <Tab label={i18n.t("settings.tabs.billing") || "Faturamento"} value={"billing"} />
            )}

          </Tabs>
        </div>

        {/* Tab Content */}
        <div className={classes.tabContent}>
          <TabPanel value={tab} name={"schedules"}>
            {loading ? (
              <div style={{ padding: 24, textAlign: "center" }}>
                <CircularProgress size={24} />
              </div>
            ) : (
              <>
                <SchedulesForm
                  loading={loading}
                  onSubmit={handleSubmitSchedules}
                  initialValues={schedules}
                />
                <ScheduleExceptionsManager />
              </>
            )}
          </TabPanel>

          <TabPanel value={tab} name={"options"}>
            <Options
              settings={settings}
              currentUser={currentUser}
              scheduleTypeChanged={(value) =>
                setSchedulesEnabled(value === "company")
              }
            />
          </TabPanel>

          <TabPanel value={tab} name={"jobRoles"}>
            <JobRolesManager />
          </TabPanel>

          <TabPanel value={tab} name={"security"}>
            <MfaPersonalManager />
            {isAdminOrSuper && company?.id && (
              <MfaPolicyManager
                companyId={company.id}
                initialPolicy={company.mfaPolicy}
              />
            )}
          </TabPanel>

          {isAdminOrSuper && (
            <TabPanel value={tab} name={"companyData"}>
              {company?.id ? (
                <CompanyDataForm
                  company={company}
                  onSaved={(updated) => {
                    // Só reflete no estado local quando o que foi salvo é a
                    // PRÓPRIA conta do usuário (o form pode editar contas irmãs
                    // do grupo via seletor — não contaminar o company atual).
                    if (updated?.id === company.id) {
                      setCompany((prev) => ({ ...prev, ...updated }));
                    }
                  }}
                />
              ) : (
                <div style={{ padding: 24, textAlign: "center" }}>
                  <CircularProgress size={24} />
                </div>
              )}
            </TabPanel>
          )}

          {isAdminOrSuper && (
            <TabPanel value={tab} name={"billing"}>
              <BillingPanel />
            </TabPanel>
          )}

        </div>
      </div>
    </MainContainer>
  );
};

export default SettingsCustom;
